/**
 * Hook: updates a document title across J-code documents via Postgres.
 * @module useUpdateDocumentTitle
 */

import { toast } from "@risalabs_frontend_org/oasis-ui-kit";
import { useState } from "react";
import { updateAllJCodeDocuments } from "../../../../api/postgreSQL/postCalls/updateJCodeDocuments";
import {
  AuthOnFile,
  GeneralOrderDetails,
} from "../../../../data-model/medicalPaOrdersModel";
import { SiblingOrdersResponse } from "../../../../data-model/siblingOrdersModel";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { updateJCodeDocumentWithNewDocumentTitle } from "../../../../utils/updateJcodesWithCorrectDocRef";
import { ToastMessages } from "../../../toasts/toastMessages";
import { generateDocumentUpdatePayloads } from "../../narLetterModal/utils/generateDocumentUpdatePayloads";

/**
 * State interface for the document title update operation.
 * @property isLoading - Indicates if the update operation is in progress
 * @property success - Indicates if the last update operation was successful
 */
interface UpdateDocumentTitleState {
  isLoading: boolean;
  success: boolean;
}

/**
 * Custom hook for updating document titles across J-code documents via Postgres.
 * Manages loading/success state, shows toast feedback, and triggers a data refetch on success.
 *
 * @param generalOrderDetails - The current order, used to derive `order_id` for payloads.
 * @param authOnFileDetails - Auth-on-file entries whose document titles may be updated.
 * @param filteredSiblingOrdersDetails - Full filtered sibling orders from context
 * @param refetchAuthOnFileDetails - Callback to refresh auth-on-file data after a successful update.
 * @returns An object containing:
 * - `updateDocumentTitle` - Function to update a document's title
 * - `updateDocumentTitleState` - Current state of the update operation (isLoading, success)
 *
 * @example
 * ```tsx
 * const { updateDocumentTitle, updateDocumentTitleState } = useUpdateDocumentTitle(
 *   generalOrderDetails?.order ?? null,
 *   authOnFileDetails?.auth_on_file ?? null,
 *   fetchAuthOnFileDetails,
 * );
 * ```
 */
export const useUpdateDocumentTitle = (
  generalOrderDetails: GeneralOrderDetails | null,
  authOnFileDetails: AuthOnFile | null,
  filteredSiblingOrdersDetails: SiblingOrdersResponse,
  refetchAuthOnFileDetails: (orderId: string, orgId: string) => Promise<void>,
) => {
  const [updateDocumentTitleState, setUpdateDocumentTitleState] =
    useState<UpdateDocumentTitleState>({
      isLoading: false,
      success: false,
    });

  /**
   * Builds update payloads from the current auth-on-file entries, sends them
   * sequentially via {@link updateAllJCodeDocuments}, and refetches on success.
   *
   * @param docId - The unique identifier of the document to update
   * @param title - The new title to set for the document
   * @param onSuccess - Callback invoked only when every J-code update succeeds
   */
  const updateDocumentTitle = async (
    docId: string,
    title: string,
    onSuccess: () => void,
  ) => {
    setUpdateDocumentTitleState({ isLoading: true, success: false });

    try {
      const updatedJCodes = updateJCodeDocumentWithNewDocumentTitle(
        authOnFileDetails?.entries ?? authOnFileDetails?.auth_entries ?? [],
        docId,
        title,
      );
      const { success } = await updateAllJCodeDocuments(
        generateDocumentUpdatePayloads(
          updatedJCodes,
          generalOrderDetails?.order_id ?? "",
          getOrgIdForFetchExternalWorklist(),
        ),
        authOnFileDetails ?? {},
        filteredSiblingOrdersDetails,
      );

      if (!success) {
        setUpdateDocumentTitleState({ isLoading: false, success: false });
        toast.error(ToastMessages.DOCUMENT_TITLE_UPDATED_ERROR);
        return;
      }

      setUpdateDocumentTitleState({ isLoading: false, success: true });
      await refetchAuthOnFileDetails(
        generalOrderDetails?.order_id ?? "",
        getOrgIdForFetchExternalWorklist(),
      );
      toast.success(ToastMessages.DOCUMENT_TITLE_UPDATED_SUCCESS);

      onSuccess?.();
    } catch {
      setUpdateDocumentTitleState({ isLoading: false, success: false });
      toast.error(ToastMessages.DOCUMENT_TITLE_UPDATED_ERROR);
    }
  };

  return {
    updateDocumentTitle,
    updateDocumentTitleState,
  };
};
